import { FeatureFlags } from '@/featureFlags/FeatureFlags';
import { handleReplicationErrors } from '@/rxdb/handleReplicationErrors';
import { getIdToken } from '@/utilities/getIdToken';

import { kind as activateComplete } from '../../actions/completions/ActivateComplete';
import { Database } from '../../Database';
import { createReplication, Replication } from '../../Replication';
import { canReplicate, State } from '../../State';
import { syncReplicationStatesWithGraphQL } from '../../syncReplicationStatesWithGraphQL';
import { Task } from '../../Task';
import { idle } from './idle';
import { initialize } from './initialize';
import { notIdle } from './notIdle';
import { suspend } from './suspend';

export const kind = 'Activate';

export function activateTask(database: Database, featureFlags: FeatureFlags): Task {
  return {
    kind,
    start: async dispatch => {
      const idToken = await getIdToken();
      let replication: Replication;
      if (featureFlags.syncFamilies) {
        replication = createReplication(database, idToken);
      } else {
        replication = await syncReplicationStatesWithGraphQL(database, idToken);
        replication.replicationStates.forEach(state => handleReplicationErrors(state));
      }
      dispatch({
        kind: activateComplete,
        replication,
      });
    },
  };
}

export function start(state: State): State {
  const goalResolvers = state.goalResolvers;

  if (!state.database) {
    return initialize(state);
  }

  if (!canReplicate(state)) {
    if (state.replication) {
      return suspend({
        ...state,
        goalResolvers,
      });
    }

    return idle({ ...state, goalResolvers });
  }

  // `canReplicate` is true
  if (!state.replication) {
    return {
      ...state,
      goalResolvers,
      nextTask: activateTask(state.database, state.featureFlags),
      whenIdle: notIdle(state),
    };
  }

  // We're already replicating
  const usingFamilies = state.replication.families.length > 0;
  if (usingFamilies !== state.featureFlags.syncFamilies) {
    // Restart replication using the latest `syncFamilies` setting.
    return suspend({ ...state, goalResolvers });
  }

  return {
    ...state,
    goalResolvers,
    nextTask: undefined,
    whenIdle: notIdle(state),
  };
}
